import { Component, Output, EventEmitter } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  BodyScrollEvent,
  ColDef,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IGetRowsParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  NewValueParams,
} from 'ag-grid-community';
import FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FileTypeConstant } from '@app/core/_constant/index.constants';
import { LoadingDialogUtilService } from 'v2/modules/shared/services/util/loading-dialog-util.service';

import {
  AssemblyOrderApiService,
  DialogConfig,
  SnackBarConfig,
} from '../../../shared';
import { UiConfirmationDialogComponent } from '../../../ui';
import { AssemblyOrderConfig } from '../../configs/assembly-order.config';
import {
  EAssemblyOrderAgField,
  EAssemblyOrderStatus,
  EAssemblyOrderType,
} from '../../interfaces/assembly-order.interface';
import { AssemblyOrderUtil } from '../../utils/assembly-order.util';

@Component({
  selector: 'lum-assembly-order-open-orders-table',
  templateUrl: './assembly-order-open-orders-table.component.html',
  styleUrls: ['./assembly-order-open-orders-table.component.scss'],
})
export class AssemblyOrderOpenOrdersTableComponent {
  @Output()
  refreshClosedOrdersEvent = new EventEmitter<boolean>();

  columnDefs :ColDef[] = AssemblyOrderConfig.openOrdersColumnDefs.map(
    (columnDef :ColDef) => {
      if (columnDef.field === EAssemblyOrderAgField.STATUS) {
        return {
          ...columnDef,
          onCellValueChanged: (event :NewValueParams) => {
            const { oldValue, newValue } = event;

            if (
              oldValue !== newValue &&
              newValue === EAssemblyOrderStatus.COMPLETED
            ) {
              this.refreshClosedOrdersEvent.emit(true);
            }
          },
        };
      }

      if (columnDef.field === EAssemblyOrderAgField.ACTIONS) {
        return {
          ...columnDef,
          cellRendererParams: {
            onExportOrder: (data :any) => this.onExportOrderEvent(data),
            onDeleteOrder: (data :any) => this.onDeleteOrderEvent(data),
          },
        };
      }

      return columnDef;
    },
  );

  gridOptions :GridOptions = {
    ...AssemblyOrderConfig.gridOptions,
    rowModelType: 'clientSide',
    pagination: false,
  };

  defaultColDef :any = {
    suppressFilter: true,
  };
  rowData :any[] = [];
  currentPage :number = 0;
  lastPage :number = 0;
  fetchingData :boolean = false;
  loading :boolean = false;

  private gridApi :GridApi;

  private onDestroy$ = new Subject();

  constructor(
    private assemblyOrderApiService :AssemblyOrderApiService,
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
    private loadingDialogService :LoadingDialogUtilService,
  ) {}

  onGridReady(params :GridReadyEvent) :void {
    this.gridApi = params.api;

    this.loadData();
  }

  onBodyScroll(event :BodyScrollEvent) :void {
    if (event.direction === 'horizontal') {
      return;
    }

    const currentScrollPosition = event.top;
    const rowHeight = this.gridOptions.rowHeight || 25;
    const maxHeight = rowHeight * this.gridApi.getDisplayedRowCount();

    if (
      currentScrollPosition >= 0.9 * maxHeight &&
      !this.fetchingData &&
      this.currentPage < this.lastPage
    ) {
      this.loadData(true);

      return;
    }
  }

  onFilterChanged(event :FilterChangedEvent) :void {
    this.currentPage = 0;
    this.lastPage = 0;

    this.gridOptions.api?.showLoadingOverlay();
    this.loadData();
  }

  loadData(append :boolean = false, refresh :boolean = false) :void {
    this.fetchingData = true;
    this.assemblyOrderApiService
      .get(this.getRequestParams(refresh))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response) => {
        this.currentPage = response.meta.current_page;
        this.lastPage = response.meta.last_page;

        if (append) {
          this.rowData = [...this.rowData, ...response.data];
        } else {
          this.rowData = response.data;
        }

        this.fetchingData = false;
        this.gridApi.setRowData(this.rowData);
        this.gridOptions.api?.hideOverlay();
      });
  }

  getServerSideDatasource() :IServerSideDatasource {
    return {
      getRows: (params :IServerSideGetRowsParams) => {
        this.assemblyOrderApiService
          .get(this.getRequestParams())
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            params.successCallback(response.data, response.meta.total);
          });
      },
    };
  }

  private onExportOrderEvent(data :any) :void {
    const type = data.type ?? EAssemblyOrderType.ASSEMBLY;
    const title =
      type === EAssemblyOrderType.ASSEMBLY ? 'Assembly Order' : 'Disassembly Order';

    const params = {
      exportType: FileTypeConstant.PDF,
      orderType: title,
    };
    this.loadingDialogService.show('Creating PDF, please wait...');
    this.assemblyOrderApiService.exportOrder(data.id, params).subscribe({
      next: (resp) => {
        FileSaver.saveAs(resp, title + '_' + data.id + '.pdf');
        this.loadingDialogService.close();
      },
      error: (resp) => {
        this.loadingDialogService.close();
        this.loadingDialogService.showDialogMessage('Error Export');
      },
    });
  }

  private onDeleteOrderEvent(data :any) :void {
    const type = data.type ?? EAssemblyOrderType.ASSEMBLY;
    const title =
      type === EAssemblyOrderType.ASSEMBLY ? 'Assembly Order' : 'Disassembly Order';

    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Delete ' + title + '?',
          message:
            'Removing this order means the linked data will no longer be accessible.',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loading = true;

          this.assemblyOrderApiService
            .delete(data.id)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.loading = false;
                this.snackBar.open(response.success, 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
                this.loadData(false, true);
              },
              error: (response) => {
                this.loading = false;
                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
            });
        }
      });
  }

  private getRequestParams(refresh :boolean = false) :{
    [param :string] :string | string[];
  } {
    const params = this.gridApi.getFilterModel() as IGetRowsParams;
    const limit = this.gridOptions.paginationPageSize || 10;
    const defaultParams = {
      limit: limit.toString(),
      statuses: 'paused,in_progress,draft,issued',
      includeAge: 'true',
      page: !refresh ? (this.currentPage + 1).toString() : '1',
      sortField: 'id',
      sortDirection: 'desc',
    };

    return AssemblyOrderUtil.mapRequestParams(params, defaultParams);
  }
}
